import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { money, periodLabel } from "@/lib/format";
import { toast } from "sonner";
import {
  X, Loader2, AlertTriangle, CheckCircle2, ArrowRight, RotateCcw,
  Lock, Unlock, Plus, ArrowUpRight, Sparkles,
} from "lucide-react";

/**
 * Period reconciliation preview — audit-first.
 *
 * Shows per-partner for the chosen period:
 *   - Attachable events (would be added to a pending payout or create a new draft)
 *   - Orphan events (period already has a paid+locked payout; need reopen or roll-forward)
 *   - Current pending/paid payout state
 *   - Primary recommended action
 *
 * All read until the admin clicks an action button. Per-row actions:
 *   - Reopen & sweep orphans into the paid statement
 *   - Roll forward orphans to the current open month
 *   - (Generate monthly is still the global "do everything attachable at once" button)
 */
export default function ReconcilePreviewModal({ period, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyRow, setBusyRow] = useState(null); // `${partner_code}:${verb}`

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/commissions/payouts/reconcile-preview/${period}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Preview failed");
      onClose?.();
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);

  async function reopenAndSweep(row) {
    if (!row.paid_payout?.id) return;
    if (!window.confirm(
      `Reopen the paid payout for ${row.partner_name} (${row.period || period}) and sweep in ` +
      `${row.orphan_events.count} late event${row.orphan_events.count === 1 ? "" : "s"} ` +
      `(${money(row.orphan_events.amount_ex_gst)} ex-GST)?\n\n` +
      `The payout flips back to Pending, totals get recomputed with the late events merged in, ` +
      `and the paid_at / payment_reference clear. You'll need to mark it paid again after confirming.`
    )) return;
    setBusyRow(`${row.partner_code}:reopen`);
    try {
      await api.post(`/commissions/payouts/${row.paid_payout.id}/reopen`);
      toast.success(`Reopened + swept ${row.orphan_events.count} event${row.orphan_events.count === 1 ? "" : "s"} for ${row.partner_name}`);
      onChanged?.();
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reopen failed");
    } finally { setBusyRow(null); }
  }

  async function rollForward(row) {
    if (!window.confirm(
      `Roll ${row.orphan_events.count} late event${row.orphan_events.count === 1 ? "" : "s"} ` +
      `(${money(row.orphan_events.amount_ex_gst)}) for ${row.partner_name} ` +
      `forward out of ${period} into the current open month?\n\n` +
      `The paid ${period} statement stays locked. Each event keeps its original paid_at date; ` +
      `only the period is flipped and a "rolled forward from ${period}" audit note is stamped.`
    )) return;
    setBusyRow(`${row.partner_code}:roll`);
    try {
      const { data } = await api.post(`/commissions/payouts/roll-forward-orphans`, {
        partner_code: row.partner_code,
        from_period: period,
      });
      toast.success(`Rolled ${data.moved_count || 0} event${(data.moved_count || 0) === 1 ? "" : "s"} forward to ${data.to_period}`);
      onChanged?.();
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Roll forward failed");
    } finally { setBusyRow(null); }
  }

  const totals = data?.totals;
  const rows = data?.rows || [];

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-slate-950/60 backdrop-blur-sm p-4"
      data-testid="reconcile-preview-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-5xl max-h-[90vh] flex flex-col rounded-2xl bg-slate-900 border border-slate-800 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-slate-800 flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-cyan-400 font-semibold">
              Reconcile · dry run
            </div>
            <h3 className="font-display text-2xl mt-1" data-testid="reconcile-title">
              {periodLabel(period)} payout preview
            </h3>
            <p className="text-sm text-slate-400 mt-1">
              Everything the accounts team needs to double-check before closing{" "}
              <span className="font-mono text-slate-300">{period}</span>. Nothing writes until you click a row action.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="reconcile-close"
            className="p-2 rounded-lg hover:bg-slate-800 text-slate-400"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        {/* Totals strip */}
        {!loading && totals && (
          <div className="px-6 py-3 border-b border-slate-800 bg-slate-950/40 grid grid-cols-2 md:grid-cols-5 gap-4 text-xs" data-testid="reconcile-totals">
            <TotalChip label="Partners" value={totals.partners} />
            <TotalChip label="Would top-up / draft" value={totals.would_create_or_top_up} tone="cyan" />
            <TotalChip label="Needs reopen / roll-fwd" value={totals.needs_reopen_or_rollforward} tone={totals.needs_reopen_or_rollforward > 0 ? "amber" : "slate"} />
            <TotalChip label="Attachable ex-GST" value={money(totals.attachable_amount_ex_gst)} />
            <TotalChip label="Orphan ex-GST" value={money(totals.orphan_amount_ex_gst)} tone={totals.orphan_amount_ex_gst > 0 ? "amber" : "slate"} />
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-12 text-center text-sm text-slate-500 inline-flex items-center gap-2 justify-center w-full">
              <Loader2 className="w-4 h-4 animate-spin" /> Computing reconciliation preview…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center">
              <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-400 mb-3" />
              <div className="text-slate-200 font-medium">Nothing to reconcile for {period}.</div>
              <div className="text-xs text-slate-500 mt-1">
                No partner has unpaid events or orphans in this period.
              </div>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="reconcile-table">
              <thead className="bg-slate-950/60 text-[11px] uppercase tracking-wider text-slate-500 font-semibold sticky top-0 z-10">
                <tr>
                  <th className="text-left px-6 py-3">Partner</th>
                  <th className="text-right px-4 py-3">Attachable</th>
                  <th className="text-right px-4 py-3">Orphan</th>
                  <th className="text-left px-4 py-3">Current payout</th>
                  <th className="text-right px-6 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {rows.map((row) => (
                  <PartnerRow
                    key={row.partner_code}
                    row={{ ...row, period }}
                    busyKey={busyRow}
                    onReopen={() => reopenAndSweep(row)}
                    onRollForward={() => rollForward(row)}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>

        <footer className="px-6 py-3 border-t border-slate-800 flex items-center justify-between gap-4 text-xs text-slate-500">
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-amber-400" />
            Row sorted by "needs attention first" — orphans at the top.
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-slate-800 text-sm hover:bg-slate-950 text-slate-300"
            data-testid="reconcile-done"
          >
            Done
          </button>
        </footer>
      </div>
    </div>
  );
}

function TotalChip({ label, value, tone = "slate" }) {
  const toneCls = {
    cyan: "text-cyan-300",
    amber: "text-amber-300",
    slate: "text-slate-200",
  }[tone] || "text-slate-200";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={`font-mono text-base ${toneCls} mt-0.5`}>{value}</div>
    </div>
  );
}

function PartnerRow({ row, busyKey, onReopen, onRollForward }) {
  const isBusy = busyKey && busyKey.startsWith(`${row.partner_code}:`);
  const action = row.recommended_action;
  return (
    <tr data-testid={`reconcile-row-${row.partner_code}`}>
      <td className="px-6 py-4">
        <div className="font-medium text-slate-100">{row.partner_name}</div>
        <div className="text-[11px] text-slate-500 font-mono mt-0.5">{row.partner_code}</div>
      </td>
      <td className="px-4 py-4 text-right">
        {row.attachable_events.count > 0 ? (
          <>
            <div className="font-mono text-cyan-300">{money(row.attachable_events.amount_ex_gst)}</div>
            <div className="text-[10px] text-slate-500 mt-0.5">
              {row.attachable_events.count} event{row.attachable_events.count === 1 ? "" : "s"}
            </div>
          </>
        ) : <span className="text-slate-600">—</span>}
      </td>
      <td className="px-4 py-4 text-right">
        {row.orphan_events.count > 0 ? (
          <>
            <div className="font-mono text-amber-300">{money(row.orphan_events.amount_ex_gst)}</div>
            <div className="text-[10px] text-amber-400/80 mt-0.5">
              {row.orphan_events.count} late
            </div>
          </>
        ) : <span className="text-slate-600">—</span>}
      </td>
      <td className="px-4 py-4">
        {row.paid_payout ? (
          <div className="inline-flex items-center gap-1.5 text-[11px]">
            <Lock className="w-3 h-3 text-emerald-400" />
            <span className="text-emerald-300 font-semibold">Paid</span>
            <span className="font-mono text-slate-500">{money(row.paid_payout.amount_total)}</span>
          </div>
        ) : row.pending_payout ? (
          <div className="inline-flex items-center gap-1.5 text-[11px]">
            <Unlock className="w-3 h-3 text-cyan-400" />
            <span className="text-cyan-300 font-semibold">
              {row.pending_payout.status === "on_hold" ? "On hold" : "Pending"}
            </span>
            <span className="font-mono text-slate-500">{money(row.pending_payout.amount_total)}</span>
          </div>
        ) : (
          <span className="text-[11px] text-slate-500">No draft</span>
        )}
      </td>
      <td className="px-6 py-4 text-right">
        <RowAction
          action={action}
          busy={isBusy}
          busyKey={busyKey}
          partnerCode={row.partner_code}
          onReopen={onReopen}
          onRollForward={onRollForward}
          hasOrphans={row.orphan_events.count > 0}
          hasAttachable={row.attachable_events.count > 0}
        />
      </td>
    </tr>
  );
}

function RowAction({ action, busy, busyKey, partnerCode, onReopen, onRollForward, hasOrphans, hasAttachable }) {
  if (action === "nothing") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
        <CheckCircle2 className="w-3 h-3" /> All clear
      </span>
    );
  }
  if (action === "top_up_pending" || action === "generate_new") {
    // These are handled by the global "Generate monthly" button — just surface
    // a hint on the row so the accounts team knows what will happen.
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 text-[11px] font-semibold"
        title="Run the Generate Monthly action to apply this change"
      >
        {action === "top_up_pending"
          ? <><ArrowUpRight className="w-3 h-3" /> Top-up on generate</>
          : <><Plus className="w-3 h-3" /> New draft on generate</>}
      </span>
    );
  }
  // reopen_and_sweep (orphans present)
  return (
    <div className="inline-flex items-center gap-2">
      <button
        data-testid={`reconcile-reopen-${partnerCode}`}
        onClick={onReopen}
        disabled={busy}
        title="Reopen the paid payout and sweep the late events into the same statement. Preserves 1-payout-per-period."
        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-slate-950 text-[11px] font-semibold"
      >
        {busyKey === `${partnerCode}:reopen`
          ? <Loader2 className="w-3 h-3 animate-spin" />
          : <RotateCcw className="w-3 h-3" />}
        Reopen & sweep
      </button>
      <button
        data-testid={`reconcile-rollforward-${partnerCode}`}
        onClick={onRollForward}
        disabled={busy}
        title="Push the late events into the current open month instead. Original paid statement stays locked."
        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-slate-700 hover:border-amber-500/50 hover:bg-slate-800 text-slate-300 text-[11px] font-semibold"
      >
        {busyKey === `${partnerCode}:roll`
          ? <Loader2 className="w-3 h-3 animate-spin" />
          : <ArrowRight className="w-3 h-3" />}
        Roll forward
      </button>
    </div>
  );
}
